import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building, MapPin, Clock, Search, Briefcase, Globe } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../components/SEO';

const API = '/api';

// Region options matching admin jobs page
const REGIONS = [
  { value: 'Europe', label: 'Europe' },
  { value: 'Middle East', label: 'Middle East' },
  { value: 'North Africa', label: 'North Africa' },
  { value: 'Sub-Saharan Africa', label: 'Sub-Saharan Africa' },
  { value: 'Asia Pacific', label: 'Asia Pacific' },
  { value: 'North America', label: 'North America' },
  { value: 'Latin America', label: 'Latin America' },
  { value: 'GCC', label: 'GCC' },
  { value: 'DACH', label: 'DACH' },
  { value: 'Levant', label: 'Levant' },
];

export const JobsPage = () => {
  const { t, language, direction } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [employmentType, setEmploymentType] = useState(searchParams.get('type') || 'all');
  const [workModel, setWorkModel] = useState(searchParams.get('model') || 'all');
  const [country, setCountry] = useState(searchParams.get('country') || 'all');
  const [region, setRegion] = useState(searchParams.get('region') || 'all');

  useEffect(() => {
    fetchJobs();
  }, [employmentType, workModel, country, region]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let url = `${API}/jobs?status=active`;
      if (employmentType && employmentType !== 'all') url += `&employment_type=${employmentType}`;
      if (workModel && workModel !== 'all') url += `&work_model=${workModel}`;
      if (country && country !== 'all') url += `&country=${country}`;
      if (region && region !== 'all') url += `&region=${encodeURIComponent(region)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs();
  };

  const countries = [...new Set(jobs.map(j => j.country))].filter(Boolean);

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'الوظائف' : 'Jobs' }
  ]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="jobs-page">
      <SEO
        title="Open Positions & Career Opportunities"
        titleAr="الوظائف الشاغرة وفرص العمل"
        description="Browse open positions across Europe, Middle East, GCC, and Africa. Find jobs in various industries including technology, finance, healthcare, and more. Apply now with WeDo HR Services."
        descriptionAr="تصفح الوظائف الشاغرة في أوروبا والشرق الأوسط ودول الخليج وأفريقيا. اعثر على وظائف في مختلف الصناعات بما في ذلك التكنولوجيا والمالية والرعاية الصحية والمزيد. قدم الآن مع ويدو."
        keywords="jobs, careers, employment, job openings, hiring, وظائف, فرص عمل, توظيف, Middle East jobs, Europe careers, GCC jobs"
        canonicalUrl="/jobs"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] mb-4">
            {t('jobs.title')}
          </h1>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            {t('jobs.subtitle')}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-[#E5E7EB] py-6 sticky top-16 md:top-20 z-40">
        <div className="container-custom">
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            {/* Search Row */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B7280]" />
                <Input
                  type="text"
                  placeholder={t('jobs.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="ps-10 input-focus"
                  data-testid="job-search-input"
                />
              </div>
              <Button type="submit" className="btn-primary" data-testid="search-jobs-btn">
                <Search className="h-4 w-4 me-2" />
                {t('jobs.filter')}
              </Button>
            </div>
            
            {/* Filter Row */}
            <div className="flex flex-col md:flex-row gap-4">
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="w-full md:w-48" data-testid="region-filter">
                  <Globe className="h-4 w-4 me-2 text-[#6B7280]" />
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {REGIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={employmentType} onValueChange={setEmploymentType}>
                <SelectTrigger className="w-full md:w-48" data-testid="employment-type-filter">
                  <SelectValue placeholder={t('jobs.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('jobs.allTypes')}</SelectItem>
                  <SelectItem value="full-time">{t('jobs.fullTime')}</SelectItem>
                  <SelectItem value="part-time">{t('jobs.partTime')}</SelectItem>
                  <SelectItem value="contract">{t('jobs.contract')}</SelectItem>
                  <SelectItem value="internship">{t('jobs.internship')}</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={workModel} onValueChange={setWorkModel}>
                <SelectTrigger className="w-full md:w-48" data-testid="work-model-filter">
                  <SelectValue placeholder={t('jobs.allModels')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('jobs.allModels')}</SelectItem>
                  <SelectItem value="onsite">{t('jobs.onsite')}</SelectItem>
                  <SelectItem value="hybrid">{t('jobs.hybrid')}</SelectItem>
                  <SelectItem value="remote">{t('jobs.remote')}</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear Filters */}
              {(region !== 'all' || employmentType !== 'all' || workModel !== 'all' || search) && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => {
                    setRegion('all');
                    setEmploymentType('all');
                    setWorkModel('all');
                    setSearch('');
                  }}
                  className="text-[#6B7280]"
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-12">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD] mx-auto"></div>
              <p className="mt-4 text-[#6B7280]">{t('common.loading')}</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16">
              <Briefcase className="h-16 w-16 text-[#E5E7EB] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#111827] mb-2">{t('jobs.noJobs')}</h3>
              <p className="text-[#6B7280] mb-6">{t('jobs.talentPoolDesc')}</p>
              <Link to="/talent-pool">
                <Button className="btn-primary" data-testid="join-talent-pool-btn">
                  {t('jobs.talentPool')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <Card key={job.job_id} className="card-hover" data-testid={`job-card-${index}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                          <Building className="h-7 w-7 text-[#33AADD]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-[#111827]">
                              {language === 'ar' && job.title_ar ? job.title_ar : job.title}
                            </h3>
                            {job.is_featured && (
                              <Badge className="bg-[#33AADD] text-white">Featured</Badge>
                            )}
                          </div>
                          <p className="text-[#6B7280] mb-3">{job.company}</p>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {language === 'ar' && job.location_ar ? job.location_ar : job.location}, {language === 'ar' && job.country_ar ? job.country_ar : job.country}
                            </span>
                            {job.region && (
                              <span className="flex items-center gap-1">
                                <Globe className="h-4 w-4" />
                                {job.region}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {job.employment_type === 'full-time' ? t('jobs.fullTime') :
                               job.employment_type === 'part-time' ? t('jobs.partTime') :
                               job.employment_type === 'contract' ? t('jobs.contract') : t('jobs.internship')}
                            </span>
                            <Badge variant="outline">
                              {job.work_model === 'onsite' ? t('jobs.onsite') :
                               job.work_model === 'hybrid' ? t('jobs.hybrid') : t('jobs.remote')}
                            </Badge>
                          </div>
                          {((language === 'ar' && job.skills_ar?.length > 0) || job.skills?.length > 0) && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {(language === 'ar' && job.skills_ar?.length > 0 
                                ? job.skills_ar 
                                : job.skills || []
                              ).slice(0, 4).map((skill, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {skill}
                                </Badge>
                              ))}
                              {(language === 'ar' && job.skills_ar?.length > 0 
                                ? job.skills_ar.length 
                                : (job.skills?.length || 0)
                              ) > 4 && (
                                <Badge variant="secondary" className="text-xs">
                                  +{(language === 'ar' && job.skills_ar?.length > 0 
                                    ? job.skills_ar.length 
                                    : (job.skills?.length || 0)) - 4}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3 lg:flex-col lg:items-end">
                        {job.salary_max && (
                          <span className="text-sm font-medium text-[#111827]">
                            {job.currency} {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()}
                          </span>
                        )}
                        <div className="flex gap-2">
                          <Link to={`/jobs/${job.job_id}`}>
                            <Button variant="outline" size="sm" data-testid={`view-job-${index}`}>
                              {t('jobs.viewDetails')}
                            </Button>
                          </Link>
                          <Link to={`/jobs/${job.job_id}/apply`}>
                            <Button size="sm" className="btn-primary" data-testid={`apply-job-${index}`}>
                              {t('jobs.apply')}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Talent Pool CTA */}
          <div className="mt-12 p-8 bg-white rounded-2xl border border-[#E5E7EB] text-center">
            <h3 className="text-2xl font-bold text-[#111827] mb-2">
              {t('jobs.talentPool')}
            </h3>
            <p className="text-[#6B7280] mb-6 max-w-lg mx-auto">
              {t('jobs.talentPoolDesc')}
            </p>
            <Link to="/talent-pool">
              <Button className="btn-primary" data-testid="talent-pool-cta-btn">
                {t('jobs.talentPool')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobsPage;
